import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
    Fingerprint,
    QrCode,
    Monitor,
    Wallet,
    Boxes,
    Cpu,
    BarChart3,
    ShieldCheck,
    CreditCard,
    Check,
    ChevronRight,
    Menu as MenuIcon,
    X,
} from "lucide-react";
import { BioButton } from "@/components/bio/BioButton";
import { LanguageToggle } from "@/components/LanguageToggle";
import { useLang } from "@/context/LangContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import { api, formatApiErrorDetail } from "@/lib/api";
import { StoreBadges } from "@/components/StoreBadges";

const LOGO = "https://biofoodsoftware.co/images/new/logo-biofood-new.png";
const HUELLA = "https://biofoodsoftware.co/images/new/huella_blanca.png";
const BIOFOOD_LOGO = "/biofood-blue.png";
const BIOFOOD_LOGO_WHITE = "/biofood-white.png";
const RAPIDPAY_LOGO = "/rapidpay-white.png";
const HERO_IMG =
    "https://images.unsplash.com/photo-1588075592806-bbe6da990469?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80";

const FEATURES = [
    { icon: Fingerprint, title: "Biometría y QR", desc: "Identificación por huella o código QR. Nunca se almacena la huella, solo un identificador seguro." },
    { icon: Monitor, title: "POS y autoservicio", desc: "Punto de venta para operadores y kiosco táctil para estudiantes. Una venta en menos de 10 segundos." },
    { icon: Wallet, title: "Portal de padres y wallet", desc: "Saldo, recargas, límites de gasto y alertas de consumo desde el celular." },
    { icon: CreditCard, title: "Pagos RapidPay Pro", desc: "Recargas digitales integradas con confirmación en tiempo real." },
    { icon: Boxes, title: "Inventario", desc: "Control de productos, stock crítico y pedidos del día por cafetería." },
    { icon: Cpu, title: "Dispensadoras inteligentes", desc: "Monitoreo de dispositivos y dispensadoras conectadas en cada sede." },
    { icon: BarChart3, title: "Analítica", desc: "Dashboards de ventas, consumo y actividad por colegio, campus y cafetería." },
    { icon: ShieldCheck, title: "Seguridad", desc: "Roles por tipo de usuario, consentimiento del acudiente y auditoría completa." },
];

const FAQS = [
    { q: "¿Se almacenan las huellas de los estudiantes?", a: "No. El sistema nunca guarda imágenes de huella; solo un identificador cifrado. Incluimos una pantalla de consentimiento del acudiente." },
    { q: "¿Qué pasa si un estudiante no tiene saldo?", a: "El POS y el kiosco avisan con claridad: saldo insuficiente, límite diario superado o producto restringido para ese estudiante." },
    { q: "¿Cómo recargan los padres?", a: "Desde el portal móvil en máximo 3 pasos: eligen el monto, el método de pago y ven el resultado. (RapidPay Pro en MODO DEMO hasta activar credenciales.)" },
    { q: "¿Funciona con varias sedes?", a: "Sí. Soporta múltiples colegios, campus y cafeterías, cada rol ve únicamente lo que le corresponde." },
];

function Nav() {
    const [open, setOpen] = useState(false);
    const { t } = useLang();
    const links = [
        { href: "#ecosistema", label: t("nav.ecosystem") },
        { href: "#funciones", label: t("nav.features") },
        { href: "#faq", label: t("nav.faq") },
        { href: "#contacto", label: t("nav.contact") },
    ];
    return (
        <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-bio-border-subtle">
            <div className="mx-auto max-w-7xl px-4 md:px-8 h-16 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                    <img src={BIOFOOD_LOGO} alt="BIOFOOD" className="h-8 w-auto" />
                </div>
                <nav className="hidden md:flex items-center gap-8">
                    {links.map((l) => (
                        <a key={l.href} href={l.href} className="text-sm font-semibold text-bio-text-secondary hover:text-bio-primary transition-colors">
                            {l.label}
                        </a>
                    ))}
                </nav>
                <div className="hidden md:flex items-center gap-3">
                    <LanguageToggle />
                    <Link to="/login"><BioButton variant="secondary" size="sm">{t("nav.login")}</BioButton></Link>
                    <a href="#contacto"><BioButton size="sm">{t("cta.demo")}</BioButton></a>
                </div>
                <button data-testid="mobile-menu-toggle" className="md:hidden p-2 text-bio-text" onClick={() => setOpen(!open)}>
                    {open ? <X className="h-6 w-6" /> : <MenuIcon className="h-6 w-6" />}
                </button>
            </div>
            {open && (
                <div className="md:hidden border-t border-bio-border-subtle bg-white px-4 py-4 space-y-3">
                    {links.map((l) => (
                        <a key={l.href} href={l.href} onClick={() => setOpen(false)} className="block text-sm font-semibold text-bio-text py-2">{l.label}</a>
                    ))}
                    <Link to="/login" className="block"><BioButton variant="secondary" className="w-full">{t("nav.login")}</BioButton></Link>
                    <a href="#contacto" onClick={() => setOpen(false)} className="block"><BioButton className="w-full">{t("cta.demo")}</BioButton></a>
                    <div className="pt-1"><LanguageToggle /></div>
                </div>
            )}
        </header>
    );
}

function DemoForm() {
    const [form, setForm] = useState({ name: "", school: "", email: "", phone: "", students: "" });
    const [loading, setLoading] = useState(false);
    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/leads", form);
            toast.success("¡Solicitud enviada!", { description: "Nuestro equipo te contactará muy pronto." });
            setForm({ name: "", school: "", email: "", phone: "", students: "" });
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={submit} data-testid="demo-form" className="bg-white rounded-bio-lg border border-bio-border-subtle shadow-bio-card p-6 md:p-8 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                    <Label className="text-sm font-semibold">Nombre</Label>
                    <Input required data-testid="lead-name" value={form.name} onChange={set("name")} placeholder="Tu nombre" className="h-12 rounded-bio bg-bio-bg border-[1.5px]" />
                </div>
                <div className="space-y-1.5">
                    <Label className="text-sm font-semibold">Colegio</Label>
                    <Input required data-testid="lead-school" value={form.school} onChange={set("school")} placeholder="Nombre del colegio" className="h-12 rounded-bio bg-bio-bg border-[1.5px]" />
                </div>
                <div className="space-y-1.5">
                    <Label className="text-sm font-semibold">Correo</Label>
                    <Input required type="email" data-testid="lead-email" value={form.email} onChange={set("email")} placeholder="correo@colegio.edu.pa" className="h-12 rounded-bio bg-bio-bg border-[1.5px]" />
                </div>
                <div className="space-y-1.5">
                    <Label className="text-sm font-semibold">Teléfono</Label>
                    <Input required data-testid="lead-phone" value={form.phone} onChange={set("phone")} placeholder="+507 6000-0000" className="h-12 rounded-bio bg-bio-bg border-[1.5px]" />
                </div>
            </div>
            <div className="space-y-1.5">
                <Label className="text-sm font-semibold">Número de estudiantes</Label>
                <Input required data-testid="lead-students" value={form.students} onChange={set("students")} placeholder="Ej. 450" className="h-12 rounded-bio bg-bio-bg border-[1.5px]" />
            </div>
            <BioButton type="submit" loading={loading} data-testid="lead-submit" className="w-full">
                Solicitar demostración
            </BioButton>
        </form>
    );
}

export default function Landing() {
    const { t } = useLang();
    return (
        <div className="min-h-screen bg-bio-bg font-poppins text-bio-text">
            <Nav />

            {/* Hero */}
            <section className="relative overflow-hidden bg-bio-hero text-white">
                <div className="mx-auto max-w-7xl px-4 md:px-8 py-16 md:py-24 grid lg:grid-cols-2 gap-12 items-center">
                    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}>
                        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 text-white text-xs font-semibold mb-5">
                            <QrCode className="h-3.5 w-3.5" /> {t("hero.badge")}
                        </span>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.1]">
                            {t("hero.title1")} <span className="text-bio-accent-light">{t("hero.title2")}</span>
                        </h1>
                        <p className="mt-5 text-lg text-white/80 leading-relaxed max-w-xl">
                            {t("hero.sub")}
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <a href="#contacto"><BioButton className="bg-white bg-none !text-bio-primary hover:bg-white shadow-lg">{t("cta.demo")}</BioButton></a>
                            <a href="#funciones"><BioButton variant="secondary" className="!bg-transparent !border-white/50 !text-white hover:!bg-white/10">{t("cta.features")} <ChevronRight className="h-4 w-4" /></BioButton></a>
                        </div>
                        <div className="mt-8">
                            <p className="text-xs font-semibold uppercase tracking-wider text-white/50 mb-3">Descarga la app para padres</p>
                            <StoreBadges dark />
                        </div>
                    </motion.div>
                    <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.1 }} className="relative">
                        <img src={HERO_IMG} alt="Estudiantes en cafetería" className="rounded-bio-lg shadow-bio-modal w-full h-72 md:h-96 object-cover" />
                        <div className="absolute -bottom-5 -left-3 md:left-6 bg-white rounded-bio-card shadow-bio-modal p-4 flex items-center gap-3">
                            <div className="h-10 w-10 rounded-bio bg-[#E8F9F8] text-[#0F807B] flex items-center justify-center"><Check className="h-5 w-5" /></div>
                            <div>
                                <p className="text-xs text-bio-text-secondary">Venta confirmada</p>
                                <p className="text-sm font-bold text-bio-text">María G. · $4.50</p>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Ecosistema */}
            <section id="ecosistema" className="mx-auto max-w-7xl px-4 md:px-8 py-16 md:py-20">
                <div className="text-center max-w-2xl mx-auto mb-12">
                    <h2 className="text-2xl md:text-3xl font-bold tracking-tight">{t("eco.title")}</h2>
                    <p className="mt-3 text-bio-text-secondary">{t("eco.sub")}</p>
                </div>
                <div id="funciones" className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                    {FEATURES.map((f, i) => (
                        <motion.div
                            key={f.title}
                            initial={{ opacity: 0, y: 14 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.4, delay: i * 0.05 }}
                            className="bg-white rounded-bio-card border border-bio-border-subtle p-6 hover:shadow-bio-card hover:-translate-y-0.5 transition-all duration-200"
                        >
                            <div className="h-12 w-12 rounded-bio bg-[#EEF7FC] text-bio-primary flex items-center justify-center mb-4">
                                <f.icon className="h-6 w-6" />
                            </div>
                            <h3 className="font-bold text-bio-text">{f.title}</h3>
                            <p className="text-sm text-bio-text-secondary mt-1.5 leading-relaxed">{f.desc}</p>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* Security band */}
            <section className="bg-white border-y border-bio-border-subtle">
                <div className="mx-auto max-w-7xl px-4 md:px-8 py-12 grid md:grid-cols-3 gap-8">
                    {[
                        { icon: ShieldCheck, t: "Privacidad por diseño", d: "Nunca almacenamos imágenes de huella, solo identificadores cifrados." },
                        { icon: Wallet, t: "Pagos seguros", d: "RapidPay Pro con confirmación en tiempo real y estados claros." },
                        { icon: BarChart3, t: "Control total", d: "Reportes por colegio, campus y cafetería con filtros avanzados." },
                    ].map((x) => (
                        <div key={x.t} className="flex gap-4">
                            <div className="h-11 w-11 shrink-0 rounded-bio bg-[#EEF7FC] text-bio-primary flex items-center justify-center"><x.icon className="h-5 w-5" /></div>
                            <div>
                                <h3 className="font-bold text-bio-text">{x.t}</h3>
                                <p className="text-sm text-bio-text-secondary mt-1">{x.d}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* FAQ */}
            <section id="faq" className="mx-auto max-w-3xl px-4 md:px-8 py-16 md:py-20">
                <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-center mb-10">Preguntas frecuentes</h2>
                <Accordion type="single" collapsible className="space-y-3">
                    {FAQS.map((f, i) => (
                        <AccordionItem key={i} value={`faq-${i}`} className="bg-white rounded-bio-card border border-bio-border-subtle px-5">
                            <AccordionTrigger className="text-left font-semibold text-bio-text hover:no-underline">{f.q}</AccordionTrigger>
                            <AccordionContent className="text-bio-text-secondary">{f.a}</AccordionContent>
                        </AccordionItem>
                    ))}
                </Accordion>
            </section>

            {/* Contacto / demo */}
            <section id="contacto" className="bg-white border-t border-bio-border-subtle">
                <div className="mx-auto max-w-5xl px-4 md:px-8 py-16 md:py-20 grid lg:grid-cols-2 gap-12 items-center">
                    <div>
                        <h2 className="text-2xl md:text-3xl font-bold tracking-tight">Solicita una demostración</h2>
                        <p className="mt-3 text-bio-text-secondary leading-relaxed">
                            Déjanos tus datos y te mostramos cómo BIOFOOD transforma la cafetería de tu colegio.
                            Ideal para directores, padres, operadores e inversionistas.
                        </p>
                        <ul className="mt-6 space-y-3">
                            {["Implementación por sede", "Capacitación del equipo", "Soporte y acompañamiento"].map((x) => (
                                <li key={x} className="flex items-center gap-3 text-sm text-bio-text"><Check className="h-4 w-4 text-bio-success" /> {x}</li>
                            ))}
                        </ul>
                    </div>
                    <DemoForm />
                </div>
            </section>

            <footer className="bg-bio-primary-dark text-white/70">
                <div className="mx-auto max-w-7xl px-4 md:px-8 py-12">
                <div className="flex flex-col md:flex-row items-center justify-between gap-8">
                    <img src={BIOFOOD_LOGO_WHITE} alt="BIOFOOD" className="h-9 w-auto" data-testid="footer-biofood-logo" />
                    <div className="flex flex-col items-center md:items-end gap-2.5">
                        <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-white/40">Procesador de pagos</span>
                        <img src={RAPIDPAY_LOGO} alt="RapidPay" className="h-7 w-auto" data-testid="footer-rapidpay-logo" />
                    </div>
                </div>
                <div className="mt-8 pt-6 border-t border-white/15 text-center text-xs leading-relaxed text-white/55">
                    © 2026 RapidPay. Todos los derechos reservados. RapidPay es un Payment Facilitator (PayFac).
                </div>
            </div>
            </footer>
        </div>
    );
}
